<template>
  <div>
    <div class="show-highlight-checkbox">
      <Checkbox
        v-model="showOnlyHighlight"
        size="large"
      >
        <span style="font-size: 14px"><b>Show Only Highlighted Tables</b></span>
      </Checkbox>
    </div>
    <div style="position:relative">
      <div class="er-diagram-buttons">
        <Tooltip content="Default Zoom">
          <Button @click="onDefaultZoomClick">
            <i class="fas fa-compress-arrows-alt fa-lg" />
          </Button>
        </Tooltip>
        <Tooltip content="Re-Center">
          <Button @click="onReCenterClick">
            <i class="far fa-dot-circle fa-lg" />
          </Button>
        </Tooltip>
        <Tooltip content="Full Screen">
          <Button @click="onFullScreenClick">
            <i class="fas fa-expand fa-lg" />
          </Button>
        </Tooltip>
      </div>
      <div
        ref="er-diagram-container"
        class="er-diagram-container"
        :style="{ height: `${containerHeight}px` }"
      />
    </div>
    <Modal
      v-model="showFullScreen"
      title="ER Diagram"
      fullscreen
      footer-hide
      class="full-screen-modal"
      @on-cancel="onFullScreenClose"
    >
      <div class="show-highlight-checkbox">
        <Checkbox
          v-model="showOnlyHighlight"
          size="large"
        >
          <span style="font-size: 14px"><b>Show Only Highlighted Tables</b></span>
        </Checkbox>
      </div>
      <div style="position:relative">
        <div class="er-diagram-buttons">
          <Tooltip content="Default Zoom">
            <Button @click="onDefaultZoomClick">
              <i class="fas fa-compress-arrows-alt fa-lg" />
            </Button>
          </Tooltip>
          <Tooltip content="Re-Center">
            <Button @click="onReCenterClick">
              <i class="far fa-dot-circle fa-lg" />
            </Button>
          </Tooltip>
        </div>
        <div
          ref="er-diagram-container-fullscreen"
          class="er-diagram-container"
          :style="{ height: `${fullScreenContainerHeight}px` }"
        />
      </div>
    </Modal>
    <TableDetailModal
      v-model="tableDetailModalData.visible"
      :tables="tableDetailModalData.tables"
      :table="tableDetailModalData.table"
    />
  </div>
</template>
<script>
import TableDetailModal from './TableDetailModal'
import cytoscape from 'cytoscape'
import coseBilkent from 'cytoscape-cose-bilkent'
import noOverlap from 'cytoscape-no-overlap'
import nodeHtmlLabel from 'cytoscape-node-html-label'

let previousClick
let clickTimeout
let cy = null // could not use this.cy because cytoscape instances do not go well with Vue data object.

cytoscape.use(coseBilkent)
cytoscape.use(noOverlap)
cytoscape.use(nodeHtmlLabel)

export default {

  components: {
    TableDetailModal
  },
  props: {
    tables: Array,
    filteredTables: Array,
    searchText: String
  },
  data () {
    return {
      displayTables: this.createDisplayTables(),
      showOnlyHighlight: false,
      showFullScreen: false,
      containerHeight: window.innerHeight > 252 ? window.innerHeight - 252 : window.innerHeight,
      fullScreenContainerHeight: window.innerHeight > 104 ? window.innerHeight - 104 : window.innerHeight,
      tableDetailModalData: {
        visible: false,
        tables: [],
        table: null
      }
    }
  },

  computed: {
    filteredDisplayTables () {
      return this.displayTables.filter(table => {
        if (this.showOnlyHighlight) {
          return table.highlighted
        }

        return true
      })
    },

    tablesWithForeignKeyRelations () {
      const tableHash = {}

      this.filteredDisplayTables.forEach(table => {
        table.columns.forEach(column => {
          if (column.foreignKey) {
            // table has foreign key
            tableHash[this.getFullTableName(table).toLowerCase()] = true

            // target table's primary key is a foreign key
            const targetTable = column.foreignKey.sourceTable.toLowerCase()

            tableHash[targetTable] = true
          }
        })
      })

      return this.tables.filter(table => tableHash[this.getFullTableName(table).toLowerCase()])
    },

    diagramNodes () {
      return this.filteredDisplayTables.map(table => {
        const tableNode = {
          data: {
            id: this.getFullTableName(table).toLowerCase(),
            name: table.name,
            database: table.database,
            schema: table.schema
          },
          classes: table.highlighted ? ['table', 'highlight'] : 'table'
        }

        let tableHtmlLabel = `<p class="table-content"><b>${table.name}</b>`
        let columnCounter = 0
        let longestLine = table.name.length

        table.columns.forEach(column => {
          columnCounter++
          let columnName = column.name

          if (column.is_primary_raw) {
            columnName += ' (PK)'
          } else if (column.foreignKey) {
            columnName += ' (FK)'
          }

          if (columnName.length > longestLine) {
            longestLine = column.name.length
          }

          tableHtmlLabel += `<br>${columnName}`
        })

        tableNode.data.htmlLabel = tableHtmlLabel
        tableNode.data.height = `${(columnCounter + 2) * 30}px`
        tableNode.data.width = `${(longestLine) * 14}px`

        return tableNode
      })
    },

    diagramEdges: function () {
      const edges = []
      const tableHash = {}

      this.filteredDisplayTables.forEach(table => {
        tableHash[this.getFullTableName(table).toLowerCase()] = true
      })

      this.tablesWithForeignKeyRelations.forEach(table => {
        const foreignKeys = table.columns.filter(column => column.foreignKey !== null)
        const targetHash = {}

        foreignKeys.forEach(foreignKey => {
          const target = foreignKey.foreignKey.sourceTable.toLowerCase()

          // TODO: Temporary check for misspelled table names in foreign key annotations.
          // Remove once a method for typo checking on snowflake side has been created.
          if (tableHash[target]) {
            if (!targetHash[target]) {
              const edge = {
                data: {
                  id: (this.getFullTableName(table) + '.' + foreignKey.name + '.foreign_key').toLowerCase(),
                  label: foreignKey.name + ' -> ' + foreignKey.foreignKey.sourceKey.toUpperCase(),
                  source: this.getFullTableName(table).toLowerCase(),
                  target
                }
              }

              targetHash[target] = edge
              edges.push(edge)
            } else {
              // If a table has multiple foreign keys pointing to the same table, we only want to edit the
              // label, no need to create new edge.
              const edge = targetHash[target]

              edge.data.label += '\n' + foreignKey.name + ' -> ' + foreignKey.foreignKey.sourceKey.toUpperCase()
            }
          }
        })
      })

      return edges
    }
  },
  watch: {
    showOnlyHighlight () {
      this.setUpERDiagram()
    },

    tables (newTables) {
      this.$set(this.tableDetailModalData, 'tables', newTables)
      this.displayTables = this.createDisplayTables()
      this.setUpERDiagram()
    }
  },

  mounted () {
    this.setUpERDiagram()
  },
  methods: {
    /**
       * Creates tables array used for cytoscape to display
       */
    createDisplayTables () {
      return this.tables.map(table => {
        const { name, database, schema, columns } = table
        const fullTableName = this.getFullTableName(table).toLowerCase()
        let highlighted = false

        if (this.searchText) {
          const filteredTable = this.filteredTables.find(filteredTable => {
            const filteredTableName = this.getFullTableName(filteredTable).toLowerCase()

            return filteredTableName === fullTableName
          })

          if (filteredTable) {
            highlighted = true
          }
        }

        return {
          name,
          database,
          schema,
          columns,
          highlighted
        }
      })
    },

    /**
       * Sets up cytoscape instance to display ER diagram
       */
    setUpERDiagram () {
      if (cy) {
        cy.destroy()
        cy = null
      }

      cy = cytoscape({
        container: this.showFullScreen ? this.$refs['er-diagram-container-fullscreen'] : this.$refs['er-diagram-container'],
        elements: {
          nodes: this.diagramNodes,
          edges: this.diagramEdges
        },
        style: [
          {
            selector: 'node',
            style: {
              'background-color': '#e8eaec',
              'border-width': 5,
              'border-color': '#404040',
              'border-style': 'solid',
              'border-opacity': 1,
              shape: 'rectangle',
              height: 'data(height)',
              width: 'data(width)'
            }
          },
          {
            selector: 'edge',
            style: {
              'width': 10,
              'label': 'data(label)',
              'font-size': '20px',
              'line-color': '#ccc',
              'curve-style': 'bezier',
              'target-arrow-color': '#AAA',
              'target-arrow-shape': 'triangle',
              'text-wrap': 'wrap'
            }
          },
          {
            selector: 'node.highlight',
            style: {
              'background-color': 'rgba(184, 233, 134, 0.9)'
            }
          },
          {
            selector: 'edge.highlight',
            style: {
              'line-color': 'rgba(184, 233, 134, 0.9)',
              'target-arrow-color': '#05c500',
              'line-style': 'solid',
              'font-weight': 'bold'
            }
          }
        ],
        layout: {
          name: 'cose-bilkent',
          fit: true,
          nodeRepulsion: 100000,
          idealEdgeLength: 2000
        },
        wheelSensitivity: 0.2,
        minZoom: 0.03,
        maxZoom: 5
      })

      cy.nodeHtmlLabel([{
        query: 'node',
        tpl: data => {
          return data.htmlLabel
        }
      }])

      cy.on('click', 'node', e => {
        this.highlightNodes(e)

        const currentClick = e.target

        if (clickTimeout && previousClick) {
          clearTimeout(clickTimeout)
        }

        if (previousClick === currentClick) {
          currentClick.trigger('doubleclick') // Have to do this because cytoscape does not provide doubleclick handler
          previousClick = null
        } else {
          clickTimeout = setTimeout(() => { previousClick = null }, 350)
          previousClick = currentClick
        }
      })

      cy.on('doubleclick', 'node', this.openTableModal.bind(this))

      cy.on('click', e => {
        if (e.target === cy) {
          this.unHighlightNodes(e)
        }
      })

      cy.ready(() => {
        // Have to use timeout because cytoscape does not render fast enough for the ER diagram to fit
        // when switching to full screen mode.
        setTimeout(() => {
          cy.fit()
        }, 500)
      })
    },

    /**
       * Highlights selected node and it's outgoing neighbors.
       * @param {event} e - event from user input
       */
    highlightNodes (e) {
      const selectedNode = e.target
      // unhighlight nodes that are highlighted
      this.unHighlightNodes(e)
      selectedNode.addClass('highlight').outgoers().addClass('highlight')

      const tableHash = {}
      this.displayTables.forEach(table => {
        const tableName = this.getFullTableName(table).toLowerCase()

        tableHash[tableName] = table
      })

      const highlightedNodes = e.cy.filter('.table.highlight')
      highlightedNodes.forEach(node => {
        const table = tableHash[node.id()]

        if (table) {
          table.highlighted = true
        }
      })
    },

    /**
       * Unhighlights all nodes.
       * @param {event} e - event from  user input
       */
    unHighlightNodes (e) {
      const highlightedNodes = e.cy.filter('.highlight')

      highlightedNodes.removeClass('highlight')

      this.displayTables.forEach(table => (table.highlighted = false))
    },

    /**
       * Shows the table modal
       * @param {event} e - event coming from user input
       */
    openTableModal (e) {
      const selectedNode = e.target
      const selectedTable = this.tables.find(table => {
        return this.getFullTableName(table).toLowerCase() === selectedNode.data('id')
      })

      this.$set(this.tableDetailModalData, 'table', selectedTable)
      this.$set(this.tableDetailModalData, 'visible', true)
    },

    /**
       * Formats table information to get full version of its name: database.schema.table
       * @param {Object} table - table to format full name with
       * @return {String} - full table name
       */
    getFullTableName (table) {
      return `${table.database}.${table.schema}.${table.name}`
    },

    /**
       * Re-centers the ER Diagram view to the center of the graph.
       */
    onReCenterClick () {
      cy.center()
    },

    /**
       * Resets to default zoom for graph.
       */
    onDefaultZoomClick () {
      cy.fit()
    },

    /**
       * Sets full screen mode
       */
    onFullScreenClick () {
      this.showFullScreen = true
      this.setUpERDiagram()
    },

    /**
       * Closes full screen mode
       */
    onFullScreenClose () {
      this.showFullScreen = false
      this.setUpERDiagram()
    }
  }
}
</script>
<style scoped>
  .er-diagram-container {
    overflow: auto;
    height: 100%;
    background-color: var(--color-white);
    border: 3px solid var(--color-black);
  }
  .er-diagram-container >>> .table-content {
    font-size: 20px;
    color: var(--color-black);
  }
  .er-diagram-buttons {
    margin-top: 16px;
    right: 0;
    z-index: 1;
    margin-right: 16px;
    position: absolute;
  }
  .show-highlight-checkbox {
    margin-bottom: 8px;
    display: flex;
    justify-content: flex-end;
  }
  .full-screen-modal >>> .ivu-modal-header {
    background-color: var(--color-green);
  }
  .full-screen-modal >>> .ivu-modal-header-inner {
    color: var(--color-white);
  }
  .full-screen-modal >>> .ivu-modal-close .ivu-icon {
    color: var(--color-white);
    font-weight: 700;
  }
</style>
